import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";

//importing custom modules
import { Button } from "../../components";
import { newUserSchema } from "../../yup";
import { getErrorMessage } from "../../utils/common";
import AdminFormFields, { AdminFormValues } from "./AdminFormFields";

// importing styles
import style from "./admin.module.css";
import { colors } from "../../theme/colors";

// importing api
import { useCreateUserMutation } from "../../services/api";
import { LabelBox } from "../../components";
import { RootState, useAppSelector } from "../../store";

type AddAdminLocationState = {
  userRole?: number;
  laboratoryAdminRoleId?: number;
};

// Defensive narrowing for router state to avoid unsafe casts and bad payloads.
const getAddAdminLocationState = (
  state: unknown
): AddAdminLocationState | null => {
  if (!state || typeof state !== "object") {
    return null;
  }
  if (!("userRole" in state)) {
    return null;
  }
  const { userRole, laboratoryAdminRoleId } = state as {
    userRole?: unknown;
    laboratoryAdminRoleId?: unknown;
  };
  const isValidUserRole =
    typeof userRole === "number" || typeof userRole === "undefined";
  const isValidLabAdminRoleId =
    typeof laboratoryAdminRoleId === "number" ||
    typeof laboratoryAdminRoleId === "undefined";
  if (!isValidUserRole || !isValidLabAdminRoleId) {
    return null;
  }
  return {
    userRole,
    laboratoryAdminRoleId,
  };
};

/**
 * Renders the create-admin form and submits a new admin user.
 *
 * Notes:
 * - Requires location state to include a `userRole` id for the new admin.
 * - Assumes current user has a health organisation id in auth state.
 * - TODO: Confirm required fields and server validation rules for user creation.
 */
const AddAdmin = () => {
  const navigation = useNavigate();
  const [createUser] = useCreateUserMutation();
  const { userDetails } = useAppSelector((state: RootState) => state.auth);
  const location = useLocation();
  const locationState = getAddAdminLocationState(location.state);
  const userRole = locationState?.userRole;
  const laboratoryAdminRoleId = locationState?.laboratoryAdminRoleId;
  const selectedHealthOrgId =
    typeof location.state?.healthOrgId === "number"
      ? location.state.healthOrgId
      : undefined;
  const selectedLaboratoryId =
    typeof location.state?.laboratoryId === "number"
      ? location.state.laboratoryId
      : undefined;

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminFormValues>({
    resolver: yupResolver(newUserSchema),
    mode: "onChange",
  });

  // Creating an admin requires auth context and a role from navigation state.
  const onSubmit = async (values: AdminFormValues) => {
    try {
      setLoading(true);

      if (
        (!userDetails && !selectedHealthOrgId && !selectedLaboratoryId) ||
        !userRole
      ) {
        setMessage({
          type: "error",
          text: "Missing user context. Please ensure you're logged in and try again.",
        });
        return;
      }
      if (laboratoryAdminRoleId === undefined) {
        setMessage({
          type: "error",
          text: "User role configuration is unavailable. Please try again.",
        });
        return;
      }
      const healthOrgId =
        userDetails?.health_org_id ?? selectedHealthOrgId ?? 0;
      const laboratoryId =
        userDetails?.laboratory_id ?? selectedLaboratoryId ?? 0;
      const isLaboratoryAdminRole = userRole === laboratoryAdminRoleId;
      const payload = {
        prefix: values.prefix,
        given_name: values.given_name,
        surname: values.surname,
        phone: values.phone ?? "",
        mail: values.mail,
        health_org_id: isLaboratoryAdminRole ? undefined : healthOrgId,
        laboratory_id: isLaboratoryAdminRole ? laboratoryId : undefined,
        user_role_id: userRole,
      };

      await createUser(payload).unwrap();
      setLoading(false);
      setMessage({
        type: "success",
        text: "Admin added successfully.",
      });
      // Allow time for the success message to be visible before navigating back.
      setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography className={style.detailHeading}>Add New Admin</Typography>
        <Typography className={style.description}>
          Enter the below details to start adding a new admin to Pegasus
          Cybersoft.
        </Typography>
        <Box className={style.flexTop}>
          <AdminFormFields register={register} errors={errors} />
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label="Cancel"
            />
            <Button
              loading={loading}
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label="Save"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddAdmin;
